import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "removeEntitlement",
    "removeDueBalance",
    "submitButton",
    "errors",
    "discountCode",
    "discountAmount",
    "discountButton",
    "subtotalInput",
    "subtotalButton",
    "actionsPanel",
    "discountPanel",
    "subtotalPanel",
    "expirationPanel",
    "writeOffConfirmationPanel",
    "confirmationEntitlementRemoved",
    "confirmationEntitlementRetained",
    "confirmWriteOffButton",
  ];
  static values = { purchaseHistoryId: Number };

  async showQuickLook(event) {
    event.preventDefault();
    const purchaseHistoryId = event.currentTarget.dataset.purchaseHistoryId;
    await this.render(purchaseHistoryId);
  }

  async render(purchaseHistoryId) {
    const response = await fetch(`/schools/packages/${purchaseHistoryId}/quick-look/edit`, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
      },
    });

    if (!response.ok) {
      alert("Unable to load package actions right now.");
      return;
    }

    const quickLook = document.querySelector("#school-payment-quick-look");
    if (!quickLook) {
      alert("Quick look container not found.");
      return;
    }

    quickLook.classList.remove("u-hidden");
    const documentBody = new DOMParser().parseFromString(await response.text(), "text/html").body;
    quickLook.innerHTML = "";
    quickLook.appendChild(documentBody);
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickLook = document.querySelector("#school-payment-quick-look");
    if (quickLook) {
      quickLook.classList.add("u-hidden");
      quickLook.innerHTML = "";
    }
  }

  validateForm() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = !(
      (this.hasRemoveEntitlementTarget && this.removeEntitlementTarget.checked) ||
      (this.hasRemoveDueBalanceTarget && this.removeDueBalanceTarget.checked)
    );
  }

  showDiscountPanel(event) {
    event.preventDefault();
    this.showActionPanel(this.discountPanelTarget);
  }

  showExpirationPanel(event) {
    event.preventDefault();
    this.showActionPanel(this.expirationPanelTarget);
  }

  showSubtotalPanel(event) {
    event.preventDefault();
    this.showActionPanel(this.subtotalPanelTarget);
    this.subtotalInputTarget.focus();
    this.subtotalInputTarget.select();
  }

  cancelAction(event) {
    event.preventDefault();
    if (this.hasDiscountPanelTarget) this.discountPanelTarget.classList.add("u-hidden");
    if (this.hasSubtotalPanelTarget) this.subtotalPanelTarget.classList.add("u-hidden");
    if (this.hasExpirationPanelTarget) this.expirationPanelTarget.classList.add("u-hidden");
    if (this.hasWriteOffConfirmationPanelTarget) {
      this.writeOffConfirmationPanelTarget.classList.add("u-hidden");
    }
    if (this.hasActionsPanelTarget) this.actionsPanelTarget.classList.remove("u-hidden");
    this.hideError();
  }

  reviewExpiration(event) {
    event.preventDefault();
    if (!(this.hasRemoveDueBalanceTarget && this.removeDueBalanceTarget.checked)) {
      this.submit(event);
      return;
    }

    const removesEntitlement =
      this.hasRemoveEntitlementTarget && this.removeEntitlementTarget.checked;
    this.confirmationEntitlementRemovedTarget.classList.toggle("u-hidden", !removesEntitlement);
    this.confirmationEntitlementRetainedTarget.classList.toggle("u-hidden", removesEntitlement);
    this.expirationPanelTarget.classList.add("u-hidden");
    this.writeOffConfirmationPanelTarget.classList.remove("u-hidden");
    this.hideError();
  }

  cancelWriteOffConfirmation(event) {
    event.preventDefault();
    this.writeOffConfirmationPanelTarget.classList.add("u-hidden");
    this.expirationPanelTarget.classList.remove("u-hidden");
    this.hideError();
  }

  async applyDiscount(event) {
    event.preventDefault();
    this.discountButtonTarget.disabled = true;
    this.hideError();

    const amountPanel = this.discountAmountTarget.closest("div[id^='package-discount-amount-']");
    const usingAmount = amountPanel && !amountPanel.classList.contains("u-hidden");
    const payload = usingAmount
      ? { amount: this.discountAmountTarget.value }
      : { discountCodeId: this.discountCodeTarget.value };

    const response = await fetch(
      `/schools/packages/${this.purchaseHistoryIdValue}/quick-look/discount`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify(payload),
      }
    );
    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      this.showError(body.error || "Unable to apply this discount.");
      this.discountButtonTarget.disabled = false;
      return;
    }

    this.closeQuickLook(event);
    window.dispatchEvent(new CustomEvent("driving-school-student-payments-v2:refresh"));
  }

  async saveSubtotal(event) {
    event.preventDefault();
    this.subtotalButtonTarget.disabled = true;
    this.hideError();

    const response = await fetch(
      `/schools/packages/${this.purchaseHistoryIdValue}/quick-look/subtotal`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({ amount: this.subtotalInputTarget.value }),
      }
    );
    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      this.showError(body.error || "Unable to change this subtotal.");
      this.subtotalButtonTarget.disabled = false;
      return;
    }

    window.dispatchEvent(new CustomEvent("driving-school-student-payments-v2:refresh"));
    await this.render(this.purchaseHistoryIdValue);
  }

  async submit(event) {
    event.preventDefault();
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasConfirmWriteOffButtonTarget) this.confirmWriteOffButtonTarget.disabled = true;
    this.hideError();

    const response = await fetch(
      `/schools/packages/${this.purchaseHistoryIdValue}/quick-look/edit`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          removeEntitlement: this.hasRemoveEntitlementTarget && this.removeEntitlementTarget.checked,
          removeDueBalance: this.hasRemoveDueBalanceTarget && this.removeDueBalanceTarget.checked,
        }),
      }
    );

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      this.showError(body.error || "Unable to update this package.");
      if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
      if (this.hasConfirmWriteOffButtonTarget) this.confirmWriteOffButtonTarget.disabled = false;
      return;
    }

    this.closeQuickLook(event);
    window.dispatchEvent(new CustomEvent("driving-school-student-payments-v2:refresh"));
  }

  showError(message) {
    if (!this.hasErrorsTarget) return;
    this.errorsTarget.textContent = message;
    this.errorsTarget.classList.remove("u-hidden");
  }

  hideError() {
    if (!this.hasErrorsTarget) return;
    this.errorsTarget.textContent = "";
    this.errorsTarget.classList.add("u-hidden");
  }

  showActionPanel(panel) {
    if (this.hasActionsPanelTarget) this.actionsPanelTarget.classList.add("u-hidden");
    if (this.hasDiscountPanelTarget) this.discountPanelTarget.classList.add("u-hidden");
    if (this.hasSubtotalPanelTarget) this.subtotalPanelTarget.classList.add("u-hidden");
    if (this.hasExpirationPanelTarget) this.expirationPanelTarget.classList.add("u-hidden");
    if (this.hasWriteOffConfirmationPanelTarget) {
      this.writeOffConfirmationPanelTarget.classList.add("u-hidden");
    }
    panel.classList.remove("u-hidden");
    this.hideError();
  }
}
